import {
  type AnalyticsAdapter,
  type AnalyticsDestination,
  type DestinationPayload,
} from './types';

/**
 * Adapter registry + the pre-wiring buffer (design D2, D4).
 *
 * Adapters are registered from app wiring at module scope — the same files
 * that own SDK init (and its ON-PREM-GATE) today — so registration ordering
 * matches SDK-singleton ordering by construction. The buffer is
 * defense-in-depth for future wiring reshuffles: events fired before
 * `markAnalyticsWiringComplete()` are held (bounded) and flushed through the
 * final adapter set; after completion, a destination with no adapter is
 * simply absent (the on-prem posture — F5: absence, not a runtime check).
 */

const BUFFER_LIMIT = 50;

type BufferedEvent = {
  route: ReadonlySet<AnalyticsDestination>;
  payloads: ReadonlyMap<AnalyticsDestination, DestinationPayload>;
};

const adapters = new Map<AnalyticsDestination, AnalyticsAdapter>();
let wiringComplete = false;
let buffered: BufferedEvent[] = [];
let droppedCount = 0;

export function registerAnalyticsAdapter(adapter: AnalyticsAdapter): void {
  adapters.set(adapter.destination, adapter);
}

/**
 * Called once by app wiring after all adapter registrations. Flushes the
 * pre-wiring buffer through the final adapter set and disables buffering.
 * On-prem wiring calls this with zero adapters registered — buffered events
 * drop by design (no destinations exist).
 */
export function markAnalyticsWiringComplete(): void {
  wiringComplete = true;
  const toFlush = buffered;
  buffered = [];
  for (const event of toFlush) {
    dispatchToAdapters(event.route, event.payloads);
  }
}

/** Fan one enriched event out — or buffer it if wiring hasn't completed. */
export function dispatchEvent(
  route: ReadonlySet<AnalyticsDestination>,
  payloads: ReadonlyMap<AnalyticsDestination, DestinationPayload>
): void {
  if (!wiringComplete) {
    if (buffered.length >= BUFFER_LIMIT) {
      droppedCount += 1;
      // eslint-disable-next-line no-console -- pre-wiring overflow is a wiring bug; surface it loudly in dev tools
      console.warn(
        `[analytics] pre-wiring buffer full (${BUFFER_LIMIT}); dropped ${droppedCount} event(s) — is markAnalyticsWiringComplete() missing?`
      );
      return;
    }
    buffered.push({ route, payloads });
    return;
  }
  dispatchToAdapters(route, payloads);
}

function dispatchToAdapters(
  route: ReadonlySet<AnalyticsDestination>,
  payloads: ReadonlyMap<AnalyticsDestination, DestinationPayload>
): void {
  for (const destination of route) {
    const adapter = adapters.get(destination);
    if (!adapter || !adapter.enabled) continue; // F5: absence/disabled ⇒ nothing emits, nothing throws
    const payload = payloads.get(destination);
    if (!payload) continue;
    try {
      adapter.send(payload);
    } catch (error) {
      // Analytics must never break the app: the wiring flush runs at _app
      // module scope, so an uncaught adapter throw would take down boot and
      // lose the remaining buffered events (ARCFE-688).
      // eslint-disable-next-line no-console -- surface SDK throws in dev tools; the app logger is not importable here (G1)
      console.error(`[analytics] ${destination} adapter send failed:`, error);
    }
  }
}

/** Test-only: reset adapters + buffer state between tests. */
export function __resetAnalyticsAdaptersForTests(): void {
  adapters.clear();
  wiringComplete = false;
  buffered = [];
  droppedCount = 0;
}
